import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Edit, Trash2, X, Save, Folder, FolderOpen, Tag, 
  ChevronRight, ChevronDown, Settings, Users, DollarSign, Calendar 
} from 'lucide-react';
import { formsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { hasCapability } from '../../lib/rbac.js';

function CategoryManagement() {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState('category'); // 'category', 'subcategory', 'sub-subcategory'
  const [parentId, setParentId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    sortOrder: 0,
    isActive: true
  });

  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['forms-categories'],
    queryFn: () => formsAPI.categories.getAll({ isActive: true }),
  });

  // Fetch subcategories
  const { data: subcategories, isLoading: subcategoriesLoading } = useQuery({
    queryKey: ['forms-subcategories'],
    queryFn: () => formsAPI.subcategories.getAll({ isActive: true }),
  });

  // Fetch sub-subcategories
  const { data: subSubcategories, isLoading: subSubcategoriesLoading } = useQuery({
    queryKey: ['forms-sub-subcategories'],
    queryFn: () => formsAPI.subSubcategories.getAll({ isActive: true }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => {
      switch (editingType) {
        case 'category':
          return formsAPI.categories.create(data);
        case 'subcategory':
          return formsAPI.subcategories.create({ ...data, categoryId: parentId });
        case 'sub-subcategory':
          return formsAPI.subSubcategories.create({ ...data, subcategoryId: parentId });
        default:
          throw new Error('Invalid editing type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-subcategories']);
      queryClient.invalidateQueries(['forms-sub-subcategories']);
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Create mutation error:', error);
      alert('Failed to create: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => {
      switch (editingType) {
        case 'category':
          return formsAPI.categories.update(id, data);
        case 'subcategory':
          return formsAPI.subcategories.update(id, data);
        case 'sub-subcategory':
          return formsAPI.subSubcategories.update(id, data);
        default:
          throw new Error('Invalid editing type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-subcategories']);
      queryClient.invalidateQueries(['forms-sub-subcategories']);
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Update mutation error:', error);
      alert('Failed to update: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, type }) => {
      switch (type) {
        case 'category':
          return formsAPI.categories.delete(id);
        case 'subcategory':
          return formsAPI.subcategories.delete(id);
        case 'sub-subcategory':
          return formsAPI.subSubcategories.delete(id);
        default:
          throw new Error('Invalid type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-subcategories']);
      queryClient.invalidateQueries(['forms-sub-subcategories']);
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      alert('Failed to delete: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const categoriesData = categories?.data?.data?.categories || [];
  const subcategoriesData = subcategories?.data?.data?.subcategories || [];
  const subSubcategoriesData = subSubcategories?.data?.data?.subSubcategories || [];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleCreate = (type, parentId = null) => {
    setEditingType(type);
    setParentId(parentId);
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item, type) => {
    setEditingType(type);
    setEditingItem(item);
    setFormData({
      name: item.name,
      slug: item.slug,
      description: item.description || '',
      icon: item.icon || '',
      color: item.color || '#3B82F6',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive
    });
    setShowModal(true);
  };

  const handleDelete = (id, type) => {
    if (confirm(`Are you sure you want to delete this ${type}?`)) {
      deleteMutation.mutate({ id, type });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      sortOrder: 0,
      isActive: true
    });
    setEditingItem(null);
    setParentId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category.slug) {
      case 'administrative-forms': return <Settings className="w-4 h-4" />;
      case 'hr-forms': return <Users className="w-4 h-4" />;
      case 'financial-forms': return <DollarSign className="w-4 h-4" />;
      case 'leave-attendance': return <Calendar className="w-4 h-4" />;
      default: return <Folder className="w-4 h-4" />;
    }
  };

  const renderCategoryTree = () => {
    if (categoriesLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {categoriesData.map((category) => {
          const categorySubcategories = subcategoriesData.filter(sub => sub.categoryId === category.id);
          
          return (
            <div key={category.id} className="space-y-1">
              {/* Category */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-1 rounded"
                    style={{ color: category.color || '#6B7280' }}
                  >
                    {getCategoryIcon(category)}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {category.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCreate('subcategory', category.id)}
                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(category, 'category')}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(category.id, 'category')}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {categorySubcategories.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCategory(category.id)}
                      className="p-1"
                    >
                      {expandedCategories.has(category.id) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Subcategories */}
              {expandedCategories.has(category.id) && (
                <div className="ml-6 space-y-1">
                  {categorySubcategories.map((subcategory) => {
                    const subcategorySubSubcategories = subSubcategoriesData.filter(sub => sub.subcategoryId === subcategory.id);
                    
                    return (
                      <div key={subcategory.id} className="space-y-1">
                        {/* Subcategory */}
                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div 
                              className="p-1 rounded"
                              style={{ color: subcategory.color || '#6B7280' }}
                            >
                              <FolderOpen className="w-3 h-3" />
                            </div>
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                                {subcategory.name}
                              </h5>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {subcategory.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCreate('sub-subcategory', subcategory.id)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(subcategory, 'subcategory')}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(subcategory.id, 'subcategory')}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {subcategorySubSubcategories.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleCategory(`sub-${subcategory.id}`)}
                                className="p-1"
                              >
                                {expandedCategories.has(`sub-${subcategory.id}`) ? (
                                  <ChevronDown className="w-3 h-3" />
                                ) : (
                                  <ChevronRight className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Sub-subcategories */}
                        {expandedCategories.has(`sub-${subcategory.id}`) && (
                          <div className="ml-4 space-y-1">
                            {subcategorySubSubcategories.map((subSubcategory) => (
                              <div key={subSubcategory.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="p-1 rounded"
                                    style={{ color: subSubcategory.color || '#6B7280' }}
                                  >
                                    <Tag className="w-3 h-3" />
                                  </div>
                                  <div>
                                    <h6 className="text-xs font-medium text-gray-900 dark:text-white">
                                      {subSubcategory.name}
                                    </h6>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {subSubcategory.description}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(subSubcategory, 'sub-subcategory')}
                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(subSubcategory.id, 'sub-subcategory')}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!hasCapability('canManageCategories')) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          You don't have permission to manage categories.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Category Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage form categories and subcategories</p>
        </div>
        <Button onClick={() => handleCreate('category')} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Category
        </Button>
      </div>

      {/* Category Tree */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Categories & Subcategories
          </h3>
          {renderCategoryTree()}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingItem ? 'Edit' : 'Create'} {editingType}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModal(false)}
                className="p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug
                </label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="Enter slug (e.g., hr-forms)"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <Input
                    value={formData.icon}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                    placeholder="Icon name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-10 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="#3B82F6"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort Order
                </label>
                <Input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {editingItem ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      {editingItem ? 'Update' : 'Create'}
                    </div>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManagement;
